import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Users, Plus, History, FileText, Link as LinkIcon, 
  AlertCircle, CheckCircle, Mail, Calendar, Copy, Trash2,
  TrendingUp, Target, Lightbulb, Brain, Layout, Palette, Focus, Download
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  Radar, 
  ResponsiveContainer,
  Legend
} from 'recharts';

const GridProfileInventory = () => {
  const { user } = useAuth();
  const ALL_TABS = ['new', 'description', 'history', 'reports', 'public-links'];
  const allowedTabs = ALL_TABS;
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  
  const [activeTab, setActiveTab] = useState(allowedTabs.includes('new') ? 'new' : (allowedTabs[0] || 'new'));
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [assessments, setAssessments] = useState([]);
  const [publicLinks, setPublicLinks] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [report, setReport] = useState(null);
  
  // Form states
  const [participantName, setParticipantName] = useState('');
  const [participantEmail, setParticipantEmail] = useState('');
  const [assessmentStarted, setAssessmentStarted] = useState(false);

  useEffect(() => {
    loadQuestions();
    loadEmployees();
    if (activeTab === 'history') {
      loadAssessments();
    }
    if (activeTab === 'public-links') {
      loadPublicLinks();
    }
  }, [activeTab]);

  const loadQuestions = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/gridprofile/questions`);
      setQuestions(response.data.questions);
      // Initialize answers as empty object (no pre-selection)
      setAnswers({});
    } catch (error) {
      console.error('Erro ao carregar perguntas:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar as perguntas',
        variant: 'destructive'
      });
    }
  };

  const startNewAssessment = async () => {
    if (!participantName.trim()) {
      toast({
        title: 'Atenção',
        description: 'Por favor, preencha o nome do participante',
        variant: 'destructive'
      });
      return;
    }

    if (participantEmail.trim()) {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${backendUrl}/api/gridprofile/assessment/start`,
          {
            participant_name: participantName.trim(),
            participant_email: participantEmail.trim()
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.email_sent) {
          toast({
            title: 'Link enviado!',
            description: `Link da avaliação enviado com sucesso para ${participantEmail}`
          });
        } else {
          toast({
            title: 'Link criado',
            description: `Link criado, mas não foi possível enviar o email para ${participantEmail}. Copie o link manualmente na aba Links Públicos.`,
            variant: 'destructive'
          });
        }
        setAssessmentStarted(false);
      } catch (error) {
        console.error('Error creating assessment link:', error);
        toast({
          title: 'Erro',
          description: 'Não foi possível criar o link da avaliação',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    setAssessmentStarted(true);
  };

  const loadEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
    }
  };

  const loadAssessments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/gridprofile/assessments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssessments(response.data);
    } catch (error) {
      console.error('Erro ao carregar avaliações:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar o histórico',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPublicLinks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/gridprofile/public-links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPublicLinks(response.data);
    } catch (error) {
      console.error('Erro ao carregar links:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAssessment = async () => {
    if (!participantName.trim()) {
      toast({
        title: 'Atenção',
        description: 'Por favor, informe o nome do participante',
        variant: 'destructive'
      });
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const answersArray = Object.keys(answers).map(questionId => ({
        question_id: parseInt(questionId),
        score: answers[questionId]
      }));

      const payload = {
        employee_id: selectedEmployee,
        participant_name: participantName,
        participant_email: participantEmail,
        answers: answersArray
      };

      const response = await axios.post(
        `${backendUrl}/api/gridprofile/assessment`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: 'Sucesso!',
        description: `Avaliação criada. Estilo Primário: ${getStyleName(response.data.primary_style)}`,
        variant: 'success'
      });

      // Reset form
      setParticipantName('');
      setParticipantEmail('');
      setSelectedEmployee(null);
      setAssessmentStarted(false);
      setActiveTab('history');
      
    } catch (error) {
      console.error('Erro ao criar avaliação:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível criar a avaliação',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePublicLink = async () => {
    if (!participantName.trim() || !participantEmail.trim()) {
      toast({
        title: 'Atenção',
        description: 'Por favor, informe nome e email',
        variant: 'destructive'
      });
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${backendUrl}/api/gridprofile/public-link`,
        {
          participant_name: participantName,
          participant_email: participantEmail
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const fullUrl = `${window.location.origin}/gridprofile/public/${response.data.token}`;
      
      // Copy to clipboard
      navigator.clipboard.writeText(fullUrl);
      
      toast({
        title: 'Link Criado!',
        description: 'Link copiado para área de transferência',
        variant: 'success'
      });

      setParticipantName('');
      setParticipantEmail('');
      loadPublicLinks();
      
    } catch (error) {
      console.error('Erro ao criar link:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível criar o link',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };
  const downloadReportPDF = async (assessmentId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${backendUrl}/api/gridprofile/assessment/${assessmentId}/pdf`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get participant name for filename
      const assessment = assessments.find(a => a.id === assessmentId);
      const filename = `GridProfile_${assessment?.participant_name || 'Relatorio'}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: 'Sucesso!',
        description: 'Relatório PDF baixado com sucesso',
        variant: 'success'
      });
      
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível baixar o relatório PDF',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const copyLinkToClipboard = (token) => {
    const fullUrl = `${window.location.origin}/gridprofile/public/${token}`;
    navigator.clipboard.writeText(fullUrl);
    toast({
      title: 'Copiado!',
      description: 'Link copiado para área de transferência',
      variant: 'success'
    });
  };

  const deletePublicLink = async (linkId) => {
    try {
      await axios.delete(`${backendUrl}/api/gridprofile/public-link/${linkId}`);
      
      toast({
        title: 'Sucesso',
        description: 'Link desativado',
        variant: 'success'
      });
      
      loadPublicLinks();
    } catch (error) {
      console.error('Erro ao desativar link:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível desativar o link',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteAssessment = async (assessmentId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta avaliação? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      await axios.delete(`${backendUrl}/api/gridprofile/assessment/${assessmentId}`);
      
      toast({
        title: 'Sucesso',
        description: 'Avaliação excluída com sucesso',
        variant: 'success'
      });
      
      loadAssessments();
    } catch (error) {
      console.error('Erro ao excluir avaliação:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.detail || 'Não foi possível excluir a avaliação',
        variant: 'destructive'
      });
    }
  };

  const viewReport = async (assessmentId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${backendUrl}/api/gridprofile/assessment/${assessmentId}/report`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReport(response.data);
      setActiveTab('reports');
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar o relatório',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStyleName = (styleId) => {
    const names = {
      reflexivo: 'Reflexivo',
      cauteloso: 'Cauteloso',
      energetico: 'Energético',
      analitico: 'Analítico',
      organizado: 'Organizado',
      criativo: 'Criativo',
      focado: 'Focado'
    };
    return names[styleId] || styleId;
  };

  const getStyleIcon = (styleId) => {
    const icons = {
      reflexivo: Brain,
      cauteloso: AlertCircle,
      energetico: TrendingUp,
      analitico: Target,
      organizado: Layout,
      criativo: Palette,
      focado: Focus
    };
    return icons[styleId] || Users;
  };

  const getStyleColor = (styleId) => {
    const colors = {
      reflexivo: 'text-blue-600 bg-blue-50',
      cauteloso: 'text-yellow-600 bg-yellow-50',
      energetico: 'text-orange-600 bg-orange-50',
      analitico: 'text-purple-600 bg-purple-50',
      organizado: 'text-green-600 bg-green-50',
      criativo: 'text-pink-600 bg-pink-50',
      focado: 'text-indigo-600 bg-indigo-50'
    };
    return colors[styleId] || 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-bold flex items-center gap-2">
                <Users className="h-6 w-6" />
                Inventário de Estilos Comportamentais GridProfile
              </CardTitle>
              <p className="text-sm text-gray-500 mt-2">
                Avaliação lúdica de 7 estilos comportamentais para autoconhecimento e desenvolvimento
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-5">
              {allowedTabs.includes('new') && <TabsTrigger value="new" className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Nova Avaliação
              </TabsTrigger>}
              {allowedTabs.includes('description') && <TabsTrigger value="description" className="flex items-center gap-2">
                <Lightbulb className="h-4 w-4" />
                Descrição
              </TabsTrigger>}
              {allowedTabs.includes('history') && <TabsTrigger value="history" className="flex items-center gap-2">
                <History className="h-4 w-4" />
                Histórico
              </TabsTrigger>}
              {allowedTabs.includes('reports') && <TabsTrigger value="reports" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Relatórios
              </TabsTrigger>}
              {allowedTabs.includes('public-links') && <TabsTrigger value="public-links" className="flex items-center gap-2">
                <LinkIcon className="h-4 w-4" />
                Links Públicos
              </TabsTrigger>}
            </TabsList>

            {/* Nova Avaliação */}
            {allowedTabs.includes('new') && <TabsContent value="new" className="space-y-6">
              {!assessmentStarted ? (
                // FORMULÁRIO INICIAL - Aparece ANTES das perguntas
                <>
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Iniciar Nova Avaliação</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Nome do Participante *</label>
                      <input
                        type="text"
                        value={participantName}
                        onChange={(e) => setParticipantName(e.target.value.replace(/\d/g, ''))}
                        className="w-full p-2 border rounded"
                        placeholder="Nome completo"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Email (opcional)</label>
                      <input
                        type="email"
                        value={participantEmail}
                        onChange={(e) => setParticipantEmail(e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="email@exemplo.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Vincular a Colaborador (opcional)</label>
                    <select
                      value={selectedEmployee || ''}
                      onChange={(e) => setSelectedEmployee(e.target.value || null)}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">Nenhum</option>
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.id}>{emp.nome}</option>
                      ))}
                    </select>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                      <AlertCircle className="h-5 w-5" />
                      Informações da Avaliação
                    </h3>
                    <div className="space-y-2 text-sm text-blue-800">
                      <p><strong>Total de questões:</strong> 30</p>
                      <p><strong>Estilos avaliados:</strong> 7 (Reflexivo, Analítico, Criativo, Executor, Estratégico, Comunicador, Colaborador)</p>
                      <p><strong>Tempo estimado:</strong> 10-15 minutos</p>
                      <p><strong>Resultado:</strong> Gráfico radar com perfil comportamental e estilos primário/secundário</p>
                      <p className="pt-2 border-t border-blue-200">
                        <strong>Importante:</strong> Este não é um teste psicológico validado, mas uma ferramenta lúdica para autoconhecimento e desenvolvimento.
                      </p>
                    </div>
                  </div>

                  <Button 
                    onClick={startNewAssessment}
                    disabled={loading || !participantName.trim()}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    size="lg"
                  >
                    {loading ? 'Enviando link...' : 'Iniciar Avaliação'}
                  </Button>
                </>
              ) : (
                // QUESTIONÁRIO - Aparece DEPOIS de clicar em "Iniciar Avaliação"
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-900 mb-2">ℹ️ Sobre o Inventário GridProfile</h3>
                    <p className="text-sm text-blue-800">
                      Este inventário avalia 7 estilos comportamentais através de 30 perguntas. 
                      Não é um teste psicológico validado, mas uma ferramenta lúdica para autoconhecimento e desenvolvimento.
                      Os resultados ajudam a entender preferências naturais de comportamento no ambiente de trabalho.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Questionário (30 perguntas)</h3>
                    <p className="text-sm text-gray-600">
                      Para cada afirmação, indique o quanto você concorda em uma escala de 1 a 5:
                    </p>
                    <div className="flex justify-between text-xs text-gray-600 px-4">
                      <span>1 = Discordo Totalmente</span>
                      <span>3 = Neutro</span>
                      <span>5 = Concordo Totalmente</span>
                    </div>

                    {questions.map((q, index) => (
                      <div key={q.id} className="border rounded-lg p-4 bg-white">
                        <div className="flex items-start gap-3">
                          <span className="font-semibold text-gray-600 min-w-[30px]">{index + 1}.</span>
                          <div className="flex-1">
                            <p className="mb-3">{q.question}</p>
                            <div className="flex gap-2 justify-between">
                              {[1, 2, 3, 4, 5].map(score => (
                                <button
                                  key={score}
                                  onClick={() => setAnswers({...answers, [q.id]: score})}
                                  className={`px-4 py-2 rounded transition-colors ${
                                    answers[q.id] === score
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-gray-100 hover:bg-gray-200'
                                  }`}
                                >
                                  {score}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button 
                    onClick={handleSubmitAssessment} 
                    disabled={loading || !participantName.trim()}
                    className="w-full"
                    size="lg"
                  >
                    {loading ? 'Processando...' : 'Concluir Avaliação'}
                  </Button>
                </>
              )}
            </TabsContent>}

            {/* Descrição */}
            {allowedTabs.includes('description') && <TabsContent value="description" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl font-bold flex items-center gap-2">
                    <Lightbulb className="h-6 w-6 text-blue-600" />
                    Sobre o Inventário GridProfile
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Metodologia */}
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                    <h3 className="text-xl font-bold text-blue-900 mb-3">📋 Metodologia</h3>
                    <div className="space-y-3 text-gray-700">
                      <p>
                        O <strong>Inventário de Estilos Comportamentais GridProfile</strong> é uma ferramenta lúdica e educacional 
                        desenvolvida para promover o autoconhecimento e o desenvolvimento pessoal e profissional.
                      </p>
                      <p>
                        <strong>Importante:</strong> Este não é um teste psicológico validado cientificamente, mas sim uma 
                        ferramenta inspirada em modelos comportamentais amplamente conhecidos, adaptada para contextos 
                        organizacionais e de desenvolvimento humano.
                      </p>
                      <p>
                        O inventário avalia <strong>7 estilos comportamentais</strong> através de <strong>30 perguntas</strong> 
                        em escala Likert (1 a 5), onde cada pergunta mede a intensidade de características específicas relacionadas 
                        aos diferentes estilos.
                      </p>
                      <p>
                        Os resultados são apresentados em um gráfico de radar (heptágono) que mostra visualmente a distribuição 
                        percentual de cada estilo no perfil do indivíduo, identificando os estilos primário e secundário.
                      </p>
                    </div>
                  </div>

                  {/* Estilos Comportamentais */}
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                      Os 7 Estilos Comportamentais
                    </h3>

                    <div className="space-y-4">
                      {/* 1. Reflexivo */}
                      <Card className="border-2 border-blue-200 hover:shadow-lg transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="p-3 bg-blue-100 rounded-full">
                              <Brain className="h-8 w-8 text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-xl font-bold text-blue-900 mb-3">1. Reflexivo</h4>
                              <p className="text-gray-700 mb-3">
                                Pessoas com estilo <strong>Reflexivo</strong> são pensativas, introspectivas e analíticas. 
                                Preferem considerar cuidadosamente todas as opções antes de tomar decisões.
                              </p>
                              <div className="bg-blue-50 p-4 rounded-lg">
                                <p className="font-semibold text-blue-900 mb-2">Características principais:</p>
                                <ul className="list-disc list-inside space-y-1 text-gray-700">
                                  <li>Necessitam de tempo para processar informações</li>
                                  <li>Valorizam a reflexão profunda antes de agir</li>
                                  <li>Preferem observar e ouvir antes de se manifestar</li>
                                  <li>Evitam decisões impulsivas</li>
                                  <li>Buscam entender o "porquê" das coisas</li>
                                </ul>
                              </div>
                              <p className="text-sm text-gray-600 mt-3 italic">
                                <strong>No trabalho:</strong> Excelentes em análise de problemas complexos, planejamento estratégico 
                                e tomada de decisões ponderadas. Podem ser vistos como lentos ou indecisos por colegas mais impulsivos.
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* 2. Cauteloso */}
                      <Card className="border-2 border-yellow-200 hover:shadow-lg transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="p-3 bg-yellow-100 rounded-full">
                              <AlertCircle className="h-8 w-8 text-yellow-600" />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-xl font-bold text-yellow-900 mb-3">2. Cauteloso</h4>
                              <p className="text-gray-700 mb-3">
                                Pessoas com estilo <strong>Cauteloso</strong> são cuidadosas, prudentes e avessas a riscos. 
                                Avaliam minuciosamente os perigos potenciais antes de se comprometerem com algo.
                              </p>
                              <div className="bg-yellow-50 p-4 rounded-lg">
                                <p className="font-semibold text-yellow-900 mb-2">Características principais:</p>
                                <ul className="list-disc list-inside space-y-1 text-gray-700">
                                  <li>Antecipam possíveis problemas e riscos</li>
                                  <li>Preferem ambientes seguros e previsíveis</li>
                                  <li>Valorizam regras, normas e procedimentos</li>
                                  <li>Evitam situações de incerteza quando possível</li>
                                  <li>Preparam-se exaustivamente para evitar surpresas</li>
                                </ul>
                              </div>
                              <p className="text-sm text-gray-600 mt-3 italic">
                                <strong>No trabalho:</strong> Essenciais em áreas de compliance, gestão de riscos e controle de qualidade. 
                                Ajudam a equipe a evitar erros custosos, mas podem ser vistos como pessimistas ou resistentes a mudanças.
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* 3. Energético */}
                      <Card className="border-2 border-orange-200 hover:shadow-lg transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="p-3 bg-orange-100 rounded-full">
                              <TrendingUp className="h-8 w-8 text-orange-600" />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-xl font-bold text-orange-900 mb-3">3. Energético</h4>
                              <p className="text-gray-700 mb-3">
                                Pessoas com estilo <strong>Energético</strong> são dinâmicas, entusiastas e otimistas. 
                                Trazem vitalidade, motivação e positividade para o ambiente.
                              </p>
                              <div className="bg-orange-50 p-4 rounded-lg">
                                <p className="font-semibold text-orange-900 mb-2">Características principais:</p>
                                <ul className="list-disc list-inside space-y-1 text-gray-700">
                                  <li>Demonstram entusiasmo e paixão pelo que fazem</li>
                                  <li>Motivam e energizam as pessoas ao redor</li>
                                  <li>Preferem ação imediata a longas deliberações</li>
                                  <li>Mantêm ritmo acelerado e múltiplas atividades</li>
                                  <li>Veem o lado positivo das situações</li>
                                </ul>
                              </div>
                              <p className="text-sm text-gray-600 mt-3 italic">
                                <strong>No trabalho:</strong> Ideais para vendas, marketing, liderança motivacional e gestão de mudanças. 
                                Podem ser vistos como impacientes ou superficiais por colegas mais analíticos.
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* 4. Analítico */}
                      <Card className="border-2 border-purple-200 hover:shadow-lg transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="p-3 bg-purple-100 rounded-full">
                              <Target className="h-8 w-8 text-purple-600" />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-xl font-bold text-purple-900 mb-3">4. Analítico</h4>
                              <p className="text-gray-700 mb-3">
                                Pessoas com estilo <strong>Analítico</strong> são lógicas, detalhistas e orientadas por dados. 
                                Focam em precisão, exatidão e fundamentação factual.
                              </p>
                              <div className="bg-purple-50 p-4 rounded-lg">
                                <p className="font-semibold text-purple-900 mb-2">Características principais:</p>
                                <ul className="list-disc list-inside space-y-1 text-gray-700">
                                  <li>Baseiam decisões em dados e evidências concretas</li>
                                  <li>Atentos a detalhes e nuances</li>
                                  <li>Buscam compreensão profunda dos sistemas</li>
                                  <li>Valorizam lógica e raciocínio objetivo</li>
                                  <li>Questionam suposições e verificam informações</li>
                                </ul>
                              </div>
                              <p className="text-sm text-gray-600 mt-3 italic">
                                <strong>No trabalho:</strong> Excelentes em análise de dados, pesquisa, engenharia e finanças. 
                                Podem ser vistos como frios ou excessivamente críticos por colegas mais intuitivos.
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* 5. Organizado */}
                      <Card className="border-2 border-green-200 hover:shadow-lg transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="p-3 bg-green-100 rounded-full">
                              <Layout className="h-8 w-8 text-green-600" />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-xl font-bold text-green-900 mb-3">5. Organizado</h4>
                              <p className="text-gray-700 mb-3">
                                Pessoas com estilo <strong>Organizado</strong> são estruturadas, metódicas e sistemáticas. 
                                Valorizam ordem, planejamento e processos bem definidos.
                              </p>
                              <div className="bg-green-50 p-4 rounded-lg">
                                <p className="font-semibold text-green-900 mb-2">Características principais:</p>
                                <ul className="list-disc list-inside space-y-1 text-gray-700">
                                  <li>Criam e seguem sistemas e processos</li>
                                  <li>Mantêm ambientes organizados e arrumados</li>
                                  <li>Planejam com antecedência e cumprem prazos</li>
                                  <li>Valorizam consistência e previsibilidade</li>
                                  <li>Documentam e estruturam informações</li>
                                </ul>
                              </div>
                              <p className="text-sm text-gray-600 mt-3 italic">
                                <strong>No trabalho:</strong> Fundamentais em gestão de projetos, operações, administração e logística. 
                                Podem ser vistos como rígidos ou inflexíveis por colegas mais espontâneos.
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* 6. Criativo */}
                      <Card className="border-2 border-pink-200 hover:shadow-lg transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="p-3 bg-pink-100 rounded-full">
                              <Palette className="h-8 w-8 text-pink-600" />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-xl font-bold text-pink-900 mb-3">6. Criativo</h4>
                              <p className="text-gray-700 mb-3">
                                Pessoas com estilo <strong>Criativo</strong> são inovadoras, imaginativas e originais. 
                                Buscam constantemente novas ideias, soluções inusitadas e expressão artística.
                              </p>
                              <div className="bg-pink-50 p-4 rounded-lg">
                                <p className="font-semibold text-pink-900 mb-2">Características principais:</p>
                                <ul className="list-disc list-inside space-y-1 text-gray-700">
                                  <li>Geram ideias originais e inovadoras</li>
                                  <li>Pensam "fora da caixa" e desafiam o status quo</li>
                                  <li>Valorizam expressão pessoal e individualidade</li>
                                  <li>Conectam conceitos aparentemente não relacionados</li>
                                  <li>Preferem variedade e novidade à rotina</li>
                                </ul>
                              </div>
                              <p className="text-sm text-gray-600 mt-3 italic">
                                <strong>No trabalho:</strong> Essenciais em design, publicidade, inovação e desenvolvimento de produtos. 
                                Podem ser vistos como desorganizados ou impráticos por colegas mais estruturados.
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* 7. Focado */}
                      <Card className="border-2 border-indigo-200 hover:shadow-lg transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="p-3 bg-indigo-100 rounded-full">
                              <Focus className="h-8 w-8 text-indigo-600" />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-xl font-bold text-indigo-900 mb-3">7. Focado</h4>
                              <p className="text-gray-700 mb-3">
                                Pessoas com estilo <strong>Focado</strong> são determinadas, concentradas e orientadas para resultados. 
                                Mantêm atenção singular em objetivos e prioridades.
                              </p>
                              <div className="bg-indigo-50 p-4 rounded-lg">
                                <p className="font-semibold text-indigo-900 mb-2">Características principais:</p>
                                <ul className="list-disc list-inside space-y-1 text-gray-700">
                                  <li>Concentram-se intensamente em metas específicas</li>
                                  <li>Eliminam distrações e mantêm disciplina</li>
                                  <li>Perseveram diante de obstáculos</li>
                                  <li>Priorizam eficiência e produtividade</li>
                                  <li>Comprometem-se profundamente com seus objetivos</li>
                                </ul>
                              </div>
                              <p className="text-sm text-gray-600 mt-3 italic">
                                <strong>No trabalho:</strong> Ideais para gestão de metas, execução de projetos críticos e funções que requerem 
                                alta concentração. Podem ser vistos como obsessivos ou inflexíveis por colegas mais versáteis.
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  {/* Nota Final */}
                  <div className="bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-200 p-6 rounded-lg">
                    <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                      <Lightbulb className="h-5 w-5 text-blue-600" />
                      Interpretação dos Resultados
                    </h3>
                    <div className="space-y-2 text-gray-700">
                      <p>
                        <strong>Importante:</strong> A maioria das pessoas apresenta uma combinação de vários estilos, 
                        com 1 ou 2 estilos predominantes (primário e secundário).
                      </p>
                      <p>
                        Não existem estilos "melhores" ou "piores" - cada um tem seus pontos fortes e áreas de desenvolvimento. 
                        O objetivo é conhecer seu perfil para potencializar suas características naturais e desenvolver 
                        competências complementares.
                      </p>
                      <p>
                        Use os resultados como ferramenta de autoconhecimento, desenvolvimento de carreira e melhoria 
                        da comunicação e colaboração em equipes.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>}

            {/* Histórico */}
            {allowedTabs.includes('history') && <TabsContent value="history" className="space-y-4">
              {loading ? (
                <p className="text-center text-gray-500">Carregando...</p>
              ) : assessments.length === 0 ? (
                <p className="text-center text-gray-500">Nenhuma avaliação encontrada</p>
              ) : (
                <div className="space-y-3">
                  {assessments.map(assessment => {
                    const StyleIcon = getStyleIcon(assessment.primary_style);
                    return (
                      <Card key={assessment.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`p-3 rounded-full ${getStyleColor(assessment.primary_style)}`}>
                                <StyleIcon className="h-6 w-6" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold">{assessment.participant_name}</h3>
                                  {!assessment.employee_id && assessment.public_link_token && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                      Link Público
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600">
                                  Estilo Primário: <span className="font-medium">{getStyleName(assessment.primary_style)}</span>
                                  {assessment.secondary_style && (
                                    <span> | Secundário: {getStyleName(assessment.secondary_style)}</span>
                                  )}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {new Date(assessment.assessment_date).toLocaleDateString('pt-BR')}
                                  {assessment.participant_email && (
                                    <span className="ml-2">• {assessment.participant_email}</span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => viewReport(assessment.id)}
                                variant="outline"
                                size="sm"
                              >
                                <FileText className="h-4 w-4 mr-2" />
                                Ver Relatório
                              </Button>
                              <Button
                                onClick={() => downloadReportPDF(assessment.id)}
                                variant="outline"
                                size="sm"
                              >
                                <Download className="h-4 w-4 mr-2" />
                                PDF
                              </Button>
                              {user?.is_admin && (
                                <Button
                                  onClick={() => handleDeleteAssessment(assessment.id)}
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>}

            {/* Relatórios */}
            {allowedTabs.includes('reports') && <TabsContent value="reports" className="space-y-6">
              {report ? (
                <div className="space-y-6">
                  <Card className={`border-2 ${getStyleColor(report.primary_style)}`}>
                    <CardHeader>
                      <div className="flex items-center gap-4">
                        {React.createElement(getStyleIcon(report.primary_style), { className: "h-12 w-12" })}
                        <div>
                          <CardTitle className="text-2xl">{report.participant_name}</CardTitle>
                          <p className="text-sm text-gray-600">
                            Avaliado em {new Date(report.assessment_date).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold mb-2">Estilo Comportamental</h3>
                        <div className="flex gap-3">
                          <span className={`px-4 py-2 rounded-full font-medium ${getStyleColor(report.primary_style)}`}>
                            Primário: {getStyleName(report.primary_style)}
                          </span>
                          {report.secondary_style && (
                            <span className={`px-4 py-2 rounded-full font-medium ${getStyleColor(report.secondary_style)}`}>
                              Secundário: {getStyleName(report.secondary_style)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Gráfico de Radar */}
                      <div className="bg-white rounded-lg p-6 border-2 border-gray-100">
                        <h3 className="text-lg font-semibold mb-4 text-center">Perfil Comportamental GridProfile</h3>
                        <ResponsiveContainer width="100%" height={400}>
                          <RadarChart data={Object.entries(report.scores).map(([style, score]) => ({
                            estilo: getStyleName(style),
                            valor: parseFloat(score.toFixed(1)),
                            fullMark: 100
                          }))}>
                            <PolarGrid stroke="#e5e7eb" />
                            <PolarAngleAxis 
                              dataKey="estilo" 
                              tick={{ fill: '#374151', fontSize: 12, fontWeight: 600 }}
                            />
                            <PolarRadiusAxis 
                              angle={90} 
                              domain={[0, 100]} 
                              tick={{ fill: '#6b7280', fontSize: 10 }}
                            />
                            <Radar
                              name="Score"
                              dataKey="valor"
                              stroke="#3b82f6"
                              fill="#3b82f6"
                              fillOpacity={0.5}
                              strokeWidth={2}
                            />
                            <Legend 
                              formatter={() => 'Pontuação (%)'} 
                              wrapperStyle={{ paddingTop: '20px' }}
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                        <p className="text-xs text-center text-gray-500 mt-4">
                          Cada eixo representa um dos 7 estilos comportamentais com valores de 0 a 100%
                        </p>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold mb-3">Scores Detalhados por Estilo</h3>
                        <div className="space-y-2">
                          {Object.entries(report.scores).map(([style, score]) => (
                            <div key={style}>
                              <div className="flex justify-between text-sm mb-1">
                                <span>{getStyleName(style)}</span>
                                <span className="font-semibold">{score.toFixed(1)}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${getStyleColor(style).split(' ')[1]}`}
                                  style={{ width: `${score}%` }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold mb-2">✨ Pontos Fortes</h3>
                        <ul className="list-disc list-inside space-y-1 text-gray-700">
                          {report.strengths.map((strength, i) => (
                            <li key={i}>{strength}</li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold mb-2">🎯 Áreas de Desenvolvimento</h3>
                        <ul className="list-disc list-inside space-y-1 text-gray-700">
                          {report.development_areas.map((area, i) => (
                            <li key={i}>{area}</li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold mb-2">💼 Estilo de Trabalho</h3>
                        <p className="text-gray-700">{report.work_style}</p>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold mb-2">🤝 Contribuição em Equipe</h3>
                        <p className="text-gray-700">{report.team_contribution}</p>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold mb-2">👑 Potencial de Liderança</h3>
                        <p className="text-gray-700">{report.leadership_potential}</p>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold mb-2">💡 Recomendações</h3>
                        <ul className="list-disc list-inside space-y-1 text-gray-700">
                          {report.recommendations.map((rec, i) => (
                            <li key={i}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="flex gap-2">
                    <Button onClick={() => setReport(null)} variant="outline">
                      Voltar ao Histórico
                    </Button>
                    <Button 
                      onClick={() => downloadReportPDF(report.id)}
                      variant="outline"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Baixar PDF
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">Selecione uma avaliação no histórico para ver o relatório</p>
                </div>
              )}
            </TabsContent>}

            {/* Links Públicos */}
            {allowedTabs.includes('public-links') && <TabsContent value="public-links" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Criar Novo Link Público</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Nome do Participante *</label>
                      <input
                        type="text"
                        value={participantName}
                        onChange={(e) => setParticipantName(e.target.value.replace(/\d/g, ''))}
                        className="w-full p-2 border rounded"
                        placeholder="Nome completo"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Email *</label>
                      <input
                        type="email"
                        value={participantEmail}
                        onChange={(e) => setParticipantEmail(e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="email@exemplo.com"
                      />
                    </div>
                  </div>
                  <Button 
                    onClick={handleCreatePublicLink}
                    disabled={loading || !participantName.trim() || !participantEmail.trim()}
                  >
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Gerar Link Público
                  </Button>
                </CardContent>
              </Card>

              <div className="space-y-3">
                <h3 className="font-semibold">Links Criados</h3>
                {publicLinks.length === 0 ? (
                  <p className="text-center text-gray-500">Nenhum link criado ainda</p>
                ) : (
                  publicLinks.map(link => (
                    <Card key={link.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Mail className="h-5 w-5 text-gray-400" />
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-semibold">{link.participant_name || link.name}</p>
                                {link.external_origin === 'gridrecruter' && (
                                  <Badge
                                    variant="outline"
                                    className="bg-indigo-50 text-indigo-700 border-indigo-300 text-[10px]"
                                    title={link.external_company_id ? `Empresa externa: ${link.external_company_id}` : 'Criado via API externa'}
                                    data-testid={`gridprofile-link-gridrecruter-badge-${link.id}`}
                                  >
                                    GridRecruter
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">{link.participant_email || link.email}</p>
                              <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                <Calendar className="h-3 w-3" />
                                {new Date(link.created_at).toLocaleDateString('pt-BR')}
                                {link.completed_at && (
                                  <span className="ml-2 text-green-600 flex items-center gap-1">
                                    <CheckCircle className="h-3 w-3" />
                                    Completado
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {link.is_active && !link.completed_at && (
                              <Button
                                onClick={() => copyLinkToClipboard(link.token)}
                                variant="outline"
                                size="sm"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            )}
                              {user?.is_admin && (
                                <Button
                                  onClick={() => deletePublicLink(link.id)}
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default GridProfileInventory;
